import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, viewChild } from '@angular/core';
import {
  FwDialogComponent,
  FwDialogContentComponent,
  FwDialogHeaderComponent,
  FwIconButtonComponent,
  FwPopoverComponent,
} from '@flywheel-io/vision';

import { DownloadService } from '../../../download.service';
import { OperationCancelPopoverBodyComponent } from '../operation-cancel-popover-body/operation-cancel-popover-body.component';
import { OperationFeedbackItemDownloadStatus } from './operation-feedback.enums';
import { OperationFeedbackItemData } from './operation-feedback.interfaces';
import { OperationFeedbackItem } from './operation-feedback-item/operation-feedback-item.component';

@Component({
  selector: 'operation-feedback',
  templateUrl: './operation-feedback.component.html',
  styleUrls: ['./operation-feedback.component.scss'],
  host: {
    'test-id': 'operation-feedback',
  },
  animations: [
    trigger('toggle', [
      transition(':enter', [style({ height: 0 }), animate('0.15s ease-in', style({ height: '*' }))]),
      transition(':leave', [style({ height: '*' }), animate('0.15s ease-in', style({ height: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FwDialogComponent,
    FwDialogContentComponent,
    FwDialogHeaderComponent,
    FwIconButtonComponent,
    FwPopoverComponent,
    OperationCancelPopoverBodyComponent,
    OperationFeedbackItem,
  ],
})
export class OperationFeedback {
  private downloadService = inject(DownloadService);

  readonly activeDownloads = this.downloadService.downloads;

  popoverTrigger = viewChild('popoverTrigger', { read: ElementRef });
  popover = viewChild(FwPopoverComponent);

  protected readonly itemsInProgressCount = computed(
    () => this.activeDownloads().filter((item) => item.status === OperationFeedbackItemDownloadStatus.Progress).length,
  );
  protected readonly finishedItemsCount = computed(() => this.activeDownloads().length - this.itemsInProgressCount());
  protected isOpen = false;

  protected closePopover(): void {
    if (!this.popover()?.isOpen()) {
      return;
    }

    this.popoverTrigger()?.nativeElement.click();
  }

  protected trackByOperationFeedbackItem(index: number, item: OperationFeedbackItemData): string {
    return item.title + item.start;
  }

  protected onCloseClick(): void {
    if (this.itemsInProgressCount()) {
      this.popoverTrigger()?.nativeElement.click();
      return;
    }

    void this.onCancelDownloads();
  }

  protected async onCancelDownloads(): Promise<void> {
    await this.downloadService.cancelDownloads();
  }

  protected async onRetryDownload(payload: OperationFeedbackItemData): Promise<void> {
    await this.downloadService.retryDownload(payload);
  }

  protected async onCancelDownload(item: OperationFeedbackItemData): Promise<void> {
    await this.downloadService.cancelDownload(item.payload.id);
  }
}
